import { useEffect } from 'react';

/**
 * Dynamic Meta Tags Component
 * Automatically generates and updates meta tags based on page content
 * Optimized for dental keywords and local SEO
 */
// Cap a meta description to 165 chars at the last word boundary.
function capDescription(text) {
  if (!text) return text;
  const max = 165;
  if ([...text].length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 80 ? slice.slice(0, lastSpace) : slice).trim();
}

export default function DynamicMetaTags({ 
  title, 
  description, 
  keywords = [],
  ogImage,
  canonical,
  type = 'website',
  author = 'Trinity Dental Arts',
  location = 'Trinity, FL'
}) {
  useEffect(() => {
    // Tell prerender bots to wait while we populate the head
    window.prerenderReady = false;

    // Update page title
    document.title = title || 'Trinity Dental Arts - Expert Dental Care in Trinity, FL';

    // Helper to update or create meta tag
    const updateMetaTag = (selector, attribute, content) => {
      if (!content) return;
      
      let meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute(attribute, content);
      } else {
        meta = document.createElement('meta');
        if (selector.includes('property=')) {
          meta.setAttribute('property', selector.split('"')[1]);
        } else if (selector.includes('name=')) {
          meta.setAttribute('name', selector.split('"')[1]);
        }
        meta.setAttribute(attribute, content);
        document.head.appendChild(meta);
      }
    };

    // Standard meta tags
    updateMetaTag('meta[name="description"]', 'content', capDescription(description));
    updateMetaTag('meta[name="keywords"]', 'content', keywords.join(', '));
    updateMetaTag('meta[name="author"]', 'content', author);
    
    // OG/Twitter description — concise 165-char cap matching the meta description
    const ogDescription = capDescription(description);

    // Canonical URL must use pathname only (no query strings/hash)
    const canonicalHref = canonical || (window.location.origin + window.location.pathname);

    // Open Graph tags for social sharing — og:url must match canonical
    updateMetaTag('meta[property="og:title"]', 'content', title);
    updateMetaTag('meta[property="og:description"]', 'content', ogDescription);
    updateMetaTag('meta[property="og:type"]', 'content', type);
    updateMetaTag('meta[property="og:url"]', 'content', canonicalHref);
    
    if (ogImage) {
      updateMetaTag('meta[property="og:image"]', 'content', ogImage);
    }

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', 'content', title);
    updateMetaTag('meta[name="twitter:description"]', 'content', ogDescription);
    
    if (ogImage) {
      updateMetaTag('meta[name="twitter:image"]', 'content', ogImage);
    }

    // Geo tags for local SEO
    updateMetaTag('meta[name="geo.region"]', 'content', 'US-FL');
    updateMetaTag('meta[name="geo.placename"]', 'content', location);
    updateMetaTag('meta[name="geo.position"]', 'content', '28.186621;-82.6310909');

    // Always set canonical to clean pathname (never includes query strings or hash)
    let link = document.querySelector('link[rel="canonical"]');
    if (link) {
      link.setAttribute('href', canonicalHref);
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalHref);
      document.head.appendChild(link);
    }

    // Robots meta
    updateMetaTag('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large');

    // Signal to Prerender.io / prerender bots that the head is fully populated
    window.prerenderReady = true;
  }, [title, description, keywords, ogImage, canonical, type, author, location]);

  return null;
}

/**
 * Generate optimized meta description from content
 */
export function generateMetaDescription(content, maxLength = 155) {
  if (!content) return '';
  
  // Remove HTML tags and extra whitespace
  const cleanContent = content
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (cleanContent.length <= maxLength) {
    return cleanContent;
  }

  // Find the last complete sentence that fits
  const truncated = cleanContent.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  
  if (lastPeriod > maxLength * 0.6) {
    return truncated.substring(0, lastPeriod + 1);
  }

  // Otherwise, truncate at last word
  const lastSpace = truncated.lastIndexOf(' ');
  return truncated.substring(0, lastSpace) + '...';
}

/**
 * Extract keywords from content
 */
export function extractKeywords(content, existingKeywords = []) {
  const dentalKeywords = [
    'dental implants', 'cosmetic dentistry', 'invisalign', 'veneers',
    'teeth whitening', 'crowns', 'bridges', 'oral health', 'dentist',
    'Trinity FL', 'Tampa Bay', 'Dr. Gjelaj', 'smile makeover',
    'all-on-4', 'emergency dentistry', 'sedation dentistry',
    'bone grafting', 'periodontal', 'tooth extraction'
  ];

  const found = [];
  const lowerContent = content.toLowerCase();

  dentalKeywords.forEach(keyword => {
    if (lowerContent.includes(keyword.toLowerCase()) && !found.includes(keyword)) {
      found.push(keyword);
    }
  });

  // Combine with existing keywords
  return [...new Set([...existingKeywords, ...found])].slice(0, 10);
}